import type {
  WikiLoadState,
  WikiPage,
  WikiPageEntry,
  WikiRevision,
  WikiPermissions,
  WikiMode,
  WikiEditorState,
  WikiDeletionState,
  WikiPageAddress,
  WikiRefreshHandler,
  WikiLinkRenderer,
  WikiRevisionComparison,
} from "../wiki-types";
import { useId, useMemo } from "react";
import { WikiPageRead } from "./wiki-page-read";
import { WikiEditor } from "../editor/wiki-editor";
import { WikiConflict } from "../editor/wiki-conflict";
import { WikiHistory } from "../history/wiki-history";
export function WikiPageContent({
  layout,
  page,
  index,
  history,
  permissions,
  mode,
  onModeChange,
  editor,
  deletion,
  mutationError,
  wikiPageHref,
  onRefresh,
  renderLink,
  revertingId,
  onRevert,
  diff,
  onDiff,
}: Readonly<{
  layout: "page" | "embedded";
  page: WikiLoadState<WikiPage | null>;
  index: WikiLoadState<readonly WikiPageEntry[]>;
  history: WikiLoadState<readonly WikiRevision[]>;
  permissions: WikiPermissions;
  mode: WikiMode;
  onModeChange(mode: WikiMode): void;
  editor: WikiEditorState;
  deletion: WikiDeletionState;
  mutationError: string | null;
  wikiPageHref: WikiPageAddress;
  onRefresh: WikiRefreshHandler;
  renderLink: WikiLinkRenderer;
  revertingId: string | null;
  onRevert(revisionId: string): void;
  diff: WikiRevisionComparison | null;
  onDiff(diff: WikiRevisionComparison): void;
}>) {
  const fieldId = useId();
  const existingPageSlugs = useMemo(
    () =>
      new Set(
        (index.status === "ready" ? index.data : []).map((entry) => entry.slug),
      ),
    [index],
  );
  const currentPage = page.status === "ready" ? page.data : null;
  const missing = page.status === "ready" && page.data === null;
  return (
    <>
      {mode === "read" && (
        <WikiPageRead
          missing={missing}
          currentPage={currentPage}
          canEdit={permissions.canEdit}
          canDelete={permissions.canDelete}
          onModeChange={onModeChange}
          renderLink={renderLink}
          revisionCount={
            history.status === "ready" ? history.data.length : null
          }
          deletion={deletion}
          mutationError={mutationError}
          fieldId={fieldId}
          wikiPageHref={wikiPageHref}
          existingPageSlugs={existingPageSlugs}
        />
      )}
      {(mode === "edit" || mode === "preview") && (
        <WikiEditor
          editor={editor}
          permissions={permissions}
          mode={mode}
          onModeChange={onModeChange}
          wikiPageHref={wikiPageHref}
          existingPageSlugs={existingPageSlugs}
          renderLink={renderLink}
          missing={missing}
          fieldId={fieldId}
        />
      )}

      {editor.conflict && (
        <WikiConflict
          conflict={editor.conflict}
          pending={editor.pending}
          canEdit={permissions.canEdit}
          onMerge={editor.onMerge}
        />
      )}

      {mode === "history" && (
        <WikiHistory
          refreshable={layout === "page"}
          history={history}
          currentRevisionId={currentPage?.currentRevisionId}
          canEdit={permissions.canEdit}
          revertingId={revertingId}
          onRevert={onRevert}
          onRefresh={onRefresh}
          renderLink={renderLink}
          diff={diff}
          onDiff={onDiff}
        />
      )}
    </>
  );
}
